import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const BoardFormModal = ({ board, cutPlanId, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    boardNumber: '',
    materialId: null,
    materialName: '',
    thickness: '',
    width: '',
    height: '',
    status: 'EMPTY'
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (board) {
      setFormData({
        boardNumber: board.board_number || '',
        materialId: board.material_id || null,
        materialName: board.material_name || '',
        thickness: board.thickness || '',
        width: board.width || '',
        height: board.height || '',
        status: board.status || 'EMPTY'
      });
    } else {
      setFormData({
        boardNumber: '',
        materialId: null,
        materialName: '',
        thickness: '',
        width: '',
        height: '',
        status: 'EMPTY'
      });
    }
  }, [board, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        boardNumber: formData.boardNumber,
        materialId: formData.materialId,
        materialName: formData.materialName,
        thickness: formData.thickness ? parseFloat(formData.thickness) : null,
        width: parseFloat(formData.width),
        height: parseFloat(formData.height),
        status: formData.status
      };

      await onSave(board?.id, data);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar chapa:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {board ? 'Editar Chapa' : 'Nova Chapa'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Número da Chapa *
            </label>
            <input
              type="text"
              name="boardNumber"
              value={formData.boardNumber}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="CH-001"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Largura (mm) *
              </label>
              <input
                type="number"
                name="width"
                value={formData.width}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="2750"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Altura (mm) *
              </label>
              <input
                type="number"
                name="height"
                value={formData.height}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="1850"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Material *
              </label>
              <input
                type="text"
                name="materialName"
                value={formData.materialName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="MDF 15mm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Espessura (mm)
              </label>
              <input
                type="number"
                name="thickness"
                value={formData.thickness}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="15"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="EMPTY">Vazia</option>
              <option value="PARTIAL">Parcial</option>
              <option value="COMPLETED">Completa</option>
              <option value="CANCELLED">Cancelada</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BoardFormModal;
